import React, { useState, useEffect, useMemo } from "react";
import { createRoot } from "react-dom/client";
import {
  GlyphProvider,
  glyphWalletConnector,
  useGlyphConfigureDynamicChains,
  StrategyType,
  WalletClientType,
} from "@use-glyph/sdk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConnectKitProvider, ConnectKitButton } from "connectkit";
import { http, type Transport } from "viem";
import { createConfig, WagmiProvider, useAccount, useSignMessage, useDisconnect } from "wagmi";

const KT_API_URL = (window as any).KT_API_URL || "";
const LOGIN_MESSAGE_PREFIX =
  "Sign in to KT's Workshop\n\nBy signing this message, you verify that you own this wallet and authorize a login session. This does not trigger a blockchain transaction or cost any gas.\n\nIssued at: ";

const queryClient = new QueryClient();

async function loginToKT(
  wallet: string,
  signMessageAsync: (args: { message: string }) => Promise<string>,
): Promise<{ accessToken: string; wallet: string; expiresAt: string }> {
  const message = LOGIN_MESSAGE_PREFIX + Date.now();
  const signature = await signMessageAsync({ message });
  const res = await fetch(`${KT_API_URL}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ wallet, message, signature }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? "Login failed");
  }
  return res.json();
}

async function createSpotSession(accessToken: string): Promise<void> {
  const res = await fetch("/api/auth/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ accessToken }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? "Session creation failed");
  }
}

function AuthGate() {
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { disconnect } = useDisconnect();
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [address]);

  const handleSignIn = async () => {
    if (!address || !signMessageAsync) return;
    setSigningIn(true);
    setError(null);
    try {
      const data = await loginToKT(address, signMessageAsync);
      localStorage.setItem(
        "spot-auth",
        JSON.stringify({
          accessToken: data.accessToken,
          wallet: data.wallet,
          expiresAt: data.expiresAt,
        }),
      );
      await createSpotSession(data.accessToken);
      window.location.reload();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Sign in failed";
      setError(message);
    } finally {
      setSigningIn(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="gate-card">
        <h2 className="gate-brand">The Spot</h2>
        <p className="gate-subtitle">Connect with Glyph to get started.</p>
        <ConnectKitButton.Custom>
          {({ show }) => (
            <button type="button" onClick={show} className="gate-btn">
              Connect with Glyph
            </button>
          )}
        </ConnectKitButton.Custom>
      </div>
    );
  }

  return (
    <div className="gate-card">
      <h2 className="gate-brand">Sign In</h2>
      <p className="gate-subtitle">Sign in to continue to The Spot.</p>
      <button
        type="button"
        onClick={handleSignIn}
        disabled={signingIn}
        className="gate-btn"
      >
        {signingIn ? "Signing in\u2026" : "Sign In"}
      </button>
      {error && <p className="gate-error">{error}</p>}
      <button
        type="button"
        onClick={() => disconnect()}
        className="gate-btn"
        style={{ background: "transparent", color: "var(--gate-muted-foreground)", fontSize: "0.8125rem" }}
      >
        Disconnect
      </button>
    </div>
  );
}

function WalletProvidersInner({ children }: { children: React.ReactNode }) {
  const { chains } = useGlyphConfigureDynamicChains();

  const wagmiConfig = useMemo(() => {
    if (!chains?.length) return null;
    return createConfig({
      chains,
      transports: chains.reduce<Record<number, Transport>>(
        (acc, chain) => {
          acc[chain.id] = http();
          return acc;
        },
        {},
      ),
      connectors: [glyphWalletConnector()],
    });
  }, [chains]);

  if (!wagmiConfig) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--gate-muted-foreground)", fontFamily: "var(--gate-font-sans)" }}>
        Loading&hellip;
      </div>
    );
  }

  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <ConnectKitProvider>
          <GlyphProvider
            strategy={StrategyType.EIP1193}
            walletClientType={WalletClientType.CONNECTKIT}
            askForSignature={false}
          >
            {children}
          </GlyphProvider>
        </ConnectKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

function App() {
  return (
    <WalletProvidersInner>
      <AuthGate />
    </WalletProvidersInner>
  );
}

const root = createRoot(document.getElementById("gate-root")!);
root.render(<App />);
